<script setup lang="ts">
import type {
  CountdownProps,
  FormInst,
  FormItemRule,
  FormRules
} from 'naive-ui'

const props = defineProps<{
  modelValue: string
}>()

const type = computed({
  get() {
    return props.modelValue
  },
  set(value: string) {
    emit('update:modelValue', value)
  }
})

const emit = defineEmits(['success', 'cancel', 'update:modelValue'])
const $toast = useToast()

const agree = ref(false)

const loginForm = ref<FormInst>()
const registerForm = ref<FormInst>()

const loginData = ref({
  account: '',
  password: ''
})

const registerData = ref({
  phone: '',
  name: '',
  password: '',
  code: '',
  reenteredPassword: ''
})

const disabledButton = ref<true | false>(false)
const countDown = ref(false)

async function onLogin() {
  loginForm.value?.validate((error) => {
    if (error) {
      $toast.error('请检查输入格式！')
      return
    }
    handleLogin()
  })
}

async function onRegister() {
  registerForm.value?.validate((error) => {
    if (error) {
      $toast.error('请检查输入格式！')
      return
    }
    handleRegister()
  })
}

const reg = ref({
  phone: /^\d{11}$/,
  name: /^[\w\u4e00-\u9fa5]{2,20}$/,
  captcha: /^\d{6}$/
})

const rules: FormRules = {
  account: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('账号不能为空')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ],
  name: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('用户名不能为空')
        }
        if (!reg.value.name.test(value)) {
          return new Error('请输入正确的用户名')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ],
  phone: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('手机号不能为空')
        }
        if (!reg.value.phone.test(value)) {
          return new Error('请输入正确的手机号')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ],
  code: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('验证码不能为空')
        }
        if (!reg.value.captcha.test(value)) {
          return new Error('请输入正确的验证码')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ],
  password: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('密码不能为空')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ],
  reenteredPassword: [
    {
      required: true,
      validator: (rule: FormItemRule, value: string) => {
        if (value.length === 0) {
          return new Error('密码不能为空')
        }
        if (value !== registerData.value.password) {
          return new Error('两次输入的密码不一致')
        }
        return true
      },
      trigger: ['input', 'blur']
    }
  ]
}

const handleLoginSuccess = async (event: {
  access_token: string
}): Promise<void> => {
  useAccessToken().value = event.access_token
  const { data: userInfo, refresh } = useAuth()
  await refresh()
  console.log(userInfo.value)
  $toast.success(`登录成功，欢迎回家，${userInfo.value?.user.name}`)
  emit('success')
}

const handleRegisterSuccess = (): void => {
  $toast.success('注册成功！')
}

const getCaptcha = (): void => {
  requestSmsCode(registerData.value.phone)
    .then((res) => {
      switch (res.code) {
        case 1:
          disabledButton.value = true
          countDown.value = true
          $toast.success('验证码已成功发送，请注意查收')
          break
        default:
          $toast.error(res.msg)
          break
      }
    })
    .catch((error) => {
      $toast.error('您的验证码发送已达上限')
    })
}

const handleLogin = () => {
  loginData.value.account = loginData.value.account.trim()
  loginData.value.password = loginData.value.password.trim()
  requestLogin(loginData.value)
    .then((res) => {
      if (res.code === 1) {
        handleLoginSuccess(res.data)
      } else {
        $toast.error(res.msg)
      }
    })
    .catch((error) => {
      $toast.error('登录失败，未知错误')
    })
}

const handleRegister = () => {
  if (!agree.value) {
    $toast.info('您必须同意用户协议和隐私政策才能注册')
    return
  }
  registerData.value.name = registerData.value.name.trim()
  registerData.value.password = registerData.value.password.trim()
  requestRegister(registerData.value)
    .then((res) => {
      switch (res.code) {
        case 0:
          $toast.error('验证码不正确！')
          break
        case 1:
          handleRegisterSuccess()
          // 注册成功后自动登录
          loginData.value.account = registerData.value.name
          loginData.value.password = registerData.value.password
          handleLogin()
          break
        default:
          $toast.error(res.msg)
          break
      }
    })
    .catch((error) => {
      $toast.error('注册失败')
    })
}

const renderCountdown: CountdownProps['render'] = ({ seconds }) => {
  return String(seconds).padStart(2, '0')
}

function countDownFinish() {
  countDown.value = false
  disabledButton.value = false
}

function cancel() {
  emit('cancel')
}
</script>

<template>
  <div class="m-login-form">
    <div class="m-login-form__header">
      <m-logo block />
    </div>
    <n-tabs
      type="segment"
      :default-value="type"
      size="large"
      justify-content="space-evenly"
    >
      <n-tab-pane name="signin" tab="登录">
        <n-form
          ref="loginForm"
          :show-label="false"
          :model="loginData"
          :rules="rules"
        >
          <n-form-item-row path="account">
            <n-input
              v-model:value="loginData.account"
              :input-props="{ autocomplete: 'username' }"
              placeholder="请输入用户名/手机号/邮箱"
            />
          </n-form-item-row>
          <n-form-item-row path="password">
            <n-input
              v-model:value="loginData.password"
              type="password"
              :input-props="{ autocomplete: 'current-password' }"
              show-password-on="click"
              placeholder="请输入密码"
            />
          </n-form-item-row>
        </n-form>
        <n-button type="primary" block strong @click="onLogin">登录</n-button>
      </n-tab-pane>
      <n-tab-pane name="signup" tab="注册">
        <n-form
          ref="registerForm"
          :show-label="false"
          :model="registerData"
          :rules="rules"
        >
          <n-form-item-row label="用户名" path="name">
            <n-input
              v-model:value="registerData.name"
              :input-props="{ autocomplete: 'username' }"
              placeholder="请输入用户名"
            />
            <MHint>
              2-20字符，支持文字、数字、英文和下划线，不能使用纯数字
            </MHint>
          </n-form-item-row>
          <n-form-item-row label="手机号" path="phone">
            <n-input
              v-model:value="registerData.phone"
              placeholder="请输入手机号"
            />
            <MHint>仅支持中国大陆手机号</MHint>
          </n-form-item-row>
          <n-form-item-row label="验证码" path="code">
            <n-input-group>
              <n-input
                v-model:value="registerData.code"
                placeholder="请输入验证码"
              />
              <n-button
                type="primary"
                :disabled="disabledButton"
                ghost
                @click="getCaptcha"
              >
                <n-countdown
                  v-if="countDown"
                  :render="renderCountdown"
                  :duration="59000"
                  @finish="countDownFinish"
                />
                <span v-else>发送验证码</span>
              </n-button>
            </n-input-group>
            <MHint>一个手机号一天只能获取10个验证码</MHint>
          </n-form-item-row>
          <n-form-item-row label="密码" path="password">
            <n-input
              v-model:value="registerData.password"
              type="password"
              :input-props="{ autocomplete: 'new-password' }"
              placeholder="请输入密码"
            />
            <MHint>密码不能少于8字符</MHint>
          </n-form-item-row>
          <n-form-item-row label="重复密码" path="reenteredPassword">
            <n-input
              v-model:value="registerData.reenteredPassword"
              type="password"
              :input-props="{ autocomplete: 'new-password' }"
              placeholder="请再输入一遍密码"
            />
            <MHint>两次密码需一致</MHint>
          </n-form-item-row>
        </n-form>
        <n-checkbox v-model:checked="agree" class="m-login-form__agree">
          已阅读并同意
          <m-link primary link to="/agree/userAgreement.html">
            《用户协议》
          </m-link>
          和
          <m-link primary link to="/agree/priv.html"> 《隐私政策》 </m-link>
        </n-checkbox>
        <n-button type="primary" block strong @click="onRegister">
          注册
        </n-button>
      </n-tab-pane>
    </n-tabs>
    <n-button block strong class="m-login-form__cancel" @click="cancel">
      取消
    </n-button>
    <div class="m-login-form__footer">
      <m-link link to="/settings/security/resetPassword">忘记密码</m-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.m-login-form {
  position: relative;
  width: 100%;
  padding: 0 24px;
  background-color: #fff;
  border-radius: 8px;
  &__agree {
    margin-bottom: 12px;
  }
  &__back {
    margin-top: 12px;
  }
  &__header {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    margin-bottom: 24px;
    pointer-events: none;
  }
  &__footer {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--color-gray-200);
  }
  &__cancel {
    margin-top: 12px;
  }
  :deep(.n-form-item .n-form-item-blank) {
    flex-direction: column;
  }
}
</style>
